/**
 * -----------------------------------------------------------------------
 * AUTO-SYNCED FROM frontend-next/src/types/surfaces.ts
 *
 * Do not edit this file directly; change the Next-side source of truth
 * and mirror here. Once the shared `@eoe/surfaces` package exists the
 * mirror goes away (per rule 18 `cross-platform-universal-code`).
 *
 * See [docs/architecture/adr/011-cra-next-surface-bridge.md](../../../docs/architecture/adr/011-cra-next-surface-bridge.md).
 * -----------------------------------------------------------------------
 */

export type SurfaceMode = "iframe" | "client" | "route";

export type SurfacePresentation = "drawer" | "modal" | "fullscreen" | "inline";

export type SurfaceKind =
  | "comments"
  | "stories"
  | "stories-compose"
  | "media-viewer";

export interface SurfaceOriginContext {
  route: string;
  scrollY: number;
}

export interface SurfaceContract<TInitial = unknown> {
  surfaceKind: SurfaceKind;
  entityType: string;
  entityId: string | number;
  presentation: SurfacePresentation;
  initial?: TInitial;
  origin: SurfaceOriginContext;
}

export type SurfaceEvent =
  | { type: "surface:close" }
  | {
      type: "surface:navigate";
      pathname: string;
      replace?: boolean;
    }
  | {
      type: "surface:mutation";
      entityType: string;
      entityId: string | number;
      kind: "create" | "update" | "delete";
      childEntityType?: string;
      childEntityId?: string | number;
    }
  | { type: "surface:dirty"; dirty: boolean }
  | { type: "surface:height"; px: number }
  | { type: "surface:ready" };

export type LegacyStoriesEvent =
  | { type: "stories:close" }
  | { type: "stories:advance" };

export type AnySurfaceInboundEvent = SurfaceEvent | LegacyStoriesEvent;

export const isSurfaceEvent = (value: unknown): value is SurfaceEvent => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as { type?: unknown };

  if (typeof candidate.type !== "string") {
    return false;
  }

  switch (candidate.type) {
    case "surface:close":
    case "surface:ready":
      return true;
    case "surface:navigate":
      return typeof (value as { pathname?: unknown }).pathname === "string";
    case "surface:mutation": {
      const m = value as {
        entityType?: unknown;
        entityId?: unknown;
        kind?: unknown;
      };
      return (
        typeof m.entityType === "string" &&
        (typeof m.entityId === "string" || typeof m.entityId === "number") &&
        (m.kind === "create" || m.kind === "update" || m.kind === "delete")
      );
    }
    case "surface:dirty":
      return typeof (value as { dirty?: unknown }).dirty === "boolean";
    case "surface:height":
      return typeof (value as { px?: unknown }).px === "number";
    default:
      return false;
  }
};

export const isLegacyStoriesEvent = (
  value: unknown
): value is LegacyStoriesEvent => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as { type?: unknown };
  return (
    candidate.type === "stories:close" || candidate.type === "stories:advance"
  );
};

export const isAnySurfaceInboundEvent = (
  value: unknown
): value is AnySurfaceInboundEvent =>
  isSurfaceEvent(value) || isLegacyStoriesEvent(value);

export interface SurfaceRegistryEntry {
  kind: SurfaceKind;
  embedPathTemplate: string;
  evolveTo: SurfaceMode;
  fullscreenDefault: boolean;
}

export const SURFACE_REGISTRY: Record<SurfaceKind, SurfaceRegistryEntry> = {
  stories: {
    kind: "stories",
    embedPathTemplate: "/stories/:entityId",
    evolveTo: "route",
    fullscreenDefault: true,
  },
  "stories-compose": {
    kind: "stories-compose",
    embedPathTemplate: "/stories/compose",
    evolveTo: "route",
    fullscreenDefault: true,
  },
  comments: {
    kind: "comments",
    embedPathTemplate: "/embed/comments/:entityType/:entityId",
    evolveTo: "client",
    fullscreenDefault: false,
  },
  "media-viewer": {
    kind: "media-viewer",
    embedPathTemplate: "/embed/media/:entityType/:entityId",
    evolveTo: "client",
    fullscreenDefault: true,
  },
};
